import { Button, List, Modal, Message, Typography } from "@arco-design/web-react";
import { GroupChannel, Member } from "@sendbird/chat/lib/__definition";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getButtonColor, getButtonTextColor } from "../../../../utils/cssVariables";
import { SYSTEM_MODERATOR_USER_ID } from "../../constants";
import {
  ensureSendbirdUsers,
  fetchPlatformAdmins,
} from "../../utils/platformUserSearch";

import "./ManageGroupAdminsDialog.css";

type Props = {
  visible: boolean;
  channelUrl: string;
  onClose: () => void;
  onUpdated: () => void;
};

type AdminRow = {
  userId: string;
  displayName: string;
  isInChannel: boolean;
  isOperator: boolean;
};

function buildAdminRows(
  platformAdmins: Awaited<ReturnType<typeof fetchPlatformAdmins>>,
  channelMembers: Member[]
): AdminRow[] {
  const memberById = new Map(channelMembers.map((member) => [member.userId, member]));

  return platformAdmins
    .filter((admin) => String(admin.id) !== SYSTEM_MODERATOR_USER_ID)
    .map((admin) => {
      const userId = String(admin.id);
      const member = memberById.get(userId);
      const displayName =
        [admin.name, admin.last_name].filter(Boolean).join(" ").trim() || admin.name;

      return {
        userId,
        displayName,
        isInChannel: Boolean(member),
        isOperator: member?.role === "operator",
      };
    })
    .sort((left, right) => {
      if (left.isOperator !== right.isOperator) {
        return left.isOperator ? -1 : 1;
      }
      return left.displayName.localeCompare(right.displayName);
    });
}

export function ManageGroupAdminsDialog({
  visible,
  channelUrl,
  onClose,
  onUpdated,
}: Props) {
  const { t } = useTranslation();
  const {
    stores: {
      sdkStore: { sdk },
      userStore,
    },
  } = useSendbirdStateContext();
  const [adminRows, setAdminRows] = useState<AdminRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [promotingUserId, setPromotingUserId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const loadAdminRows = useCallback(async () => {
    if (!channelUrl) {
      return;
    }

    const [platformAdmins, channel] = await Promise.all([
      fetchPlatformAdmins(),
      sdk.groupChannel.getChannel(channelUrl) as Promise<GroupChannel>,
    ]);

    setAdminRows(buildAdminRows(platformAdmins, channel.members ?? []));
  }, [channelUrl, sdk.groupChannel]);

  useEffect(() => {
    const load = async () => {
      if (!visible || !channelUrl) {
        return;
      }

      setIsLoading(true);
      try {
        await loadAdminRows();
      } catch {
        setAdminRows([]);
      } finally {
        setIsLoading(false);
      }
    };

    load().catch(console.error);
  }, [visible, channelUrl, loadAdminRows]);

  const operatorCount = adminRows.filter((row) => row.isOperator).length;

  const handleRemoveAdmin = async (row: AdminRow) => {
    if (!row.isOperator) {
      return;
    }

    if (operatorCount <= 1) {
      Message.error({
        content: t("messages.at_least_one_admin_required"),
        closable: true,
        duration: 4000,
      });
      return;
    }

    setRemovingUserId(row.userId);
    try {
      const channel = await sdk.groupChannel.getChannel(channelUrl);
      await channel.removeOperators([row.userId]);
      await loadAdminRows();
      onUpdated();
      Message.success({
        content: t("messages.remove_group_admin_success"),
        closable: true,
        duration: 3000,
      });
    } catch {
      Message.error({
        content: t("messages.remove_group_admin_error"),
        closable: true,
        duration: 4000,
      });
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleAddAdmin = async (row: AdminRow) => {
    if (row.isOperator) {
      return;
    }

    setPromotingUserId(row.userId);
    try {
      const channel = await sdk.groupChannel.getChannel(channelUrl);

      if (!row.isInChannel) {
        await ensureSendbirdUsers([row.userId]);
        await channel.inviteWithUserIds([row.userId]);
      }

      await channel.addOperators([row.userId]);
      await loadAdminRows();
      onUpdated();
      Message.success({
        content: t("messages.promote_admin_success"),
        closable: true,
        duration: 3000,
      });
    } catch {
      Message.error({
        content: t("messages.promote_admin_error"),
        closable: true,
        duration: 4000,
      });
    } finally {
      setPromotingUserId(null);
    }
  };

  const currentUserId = userStore.user?.userId;

  return (
    <Modal
      title={t("messages.manage_admins_modal_title")}
      visible={visible}
      onCancel={onClose}
      className="manage-group-admins-dialog"
      footer={null}
    >
      <div className="manage-group-admins-dialog__content">
        <List
          className="manage-group-admins-dialog__list list-demo-actions"
          loading={isLoading}
          size="small"
          bordered={false}
          dataSource={adminRows}
          render={(row) => {
            const isSelf = row.userId === currentUserId;
            const canAddAdmin = !row.isOperator;
            const canRemoveAdmin = row.isOperator && operatorCount > 1;

            return (
              <List.Item key={row.userId}>
                <div className="manage-group-admins-dialog__row">
                  <span className="manage-group-admins-dialog__member-name">
                    {row.displayName}
                  </span>
                  <div className="manage-group-admins-dialog__actions">
                    {isSelf && (
                      <span className="manage-group-admins-dialog__pill manage-group-admins-dialog__pill--outline">
                        {t("messages.you_label")}
                      </span>
                    )}
                    {row.isOperator && !canRemoveAdmin && (
                      <span className="manage-group-admins-dialog__pill manage-group-admins-dialog__pill--outline manage-group-admins-dialog__pill--admin">
                        {t("messages.group_admin_badge")}
                      </span>
                    )}
                    {canRemoveAdmin && (
                      <Button
                        className="manage-group-admins-dialog__remove-button"
                        size="mini"
                        shape="round"
                        loading={removingUserId === row.userId}
                        onClick={() => handleRemoveAdmin(row)}
                      >
                        {t("messages.remove_from_admin_button")}
                      </Button>
                    )}
                    {canAddAdmin && (
                      <Button
                        className="manage-group-admins-dialog__promote-button"
                        style={{
                          color: getButtonTextColor(),
                          backgroundColor: getButtonColor(),
                        }}
                        size="mini"
                        shape="round"
                        loading={promotingUserId === row.userId}
                        onClick={() => handleAddAdmin(row)}
                      >
                        {t("messages.promote_to_admin_button")}
                      </Button>
                    )}
                  </div>
                </div>
              </List.Item>
            );
          }}
          noDataElement={
            <div className="manage-group-admins-dialog__empty">
              {t("messages.manage_admins_empty")}
            </div>
          }
        />
        {operatorCount === 0 && adminRows.length > 0 && (
          <Typography.Text type="secondary" className="manage-group-admins-dialog__warning">
            {t("messages.at_least_one_admin_required")}
          </Typography.Text>
        )}
      </div>
    </Modal>
  );
}
